/**
 * Which deployment ids a cloud's installer can name resources after.
 *
 * `deploymentIdProblem` is AWS's rule, read off two real AWS failures, and it
 * is the floor everywhere: on Azure it happens to keep every run clear of the
 * installer's id rewrite as well (see `azureDeploymentIdProblem`). Azure adds
 * one limit of its own on top, and only that limit.
 */
import { forCloud } from '../tenancy/cloud-account.js';
import { deploymentIdProblem } from './config.js';
/**
 * The longest id Azure's resource names survive, one below the shared 18.
 *
 * The installer names the instance's storage account `{id}qrveysa`
 * (`locals.tf:92` in image `9.6-20261007-3555`), and Azure caps storage account
 * names at 24 characters, so 24 − 7 = 17. `{id}synapse` (`locals.tf:151`) is
 * held to the same 24 where a Synapse workspace is created. That is Azure's
 * documented limit rather than an observed failure, because no Azure run has
 * been made: `docs/azure/installer-contract.md` has the reading.
 *
 * `newDeploymentIdProblem` already keeps a *new* instance at 16, so what this
 * refuses is an existing id of 17 or 18 characters — one that would pass the
 * shared rule and then die at the storage account's creation, an apply in.
 */
export const MAX_AZURE_DEPLOYMENT_ID_LENGTH = 17;
/**
 * What is wrong with a deployment id on Azure, if anything.
 *
 * The shared rule first, unchanged and with its own wording, because on Azure
 * it is load-bearing for a reason it does not mention: the installer lowercases
 * a given id and strips `-` and `_` (`locals.tf:15`), where AWS passes it
 * through. Every id `deploymentIdProblem` accepts is one that rewrite leaves
 * alone, so this control plane and the instance's own resources never name it
 * two ways. Dropping it here as an AWS-only rule would lose that.
 *
 * Then the cap. Checked after, so an id that is too long *and* has a hyphen
 * names the hyphen — the fault the shared rule's message explains — and an id
 * only Azure refuses is told it is Azure's storage account that refused it.
 */
export const azureDeploymentIdProblem = (value) => {
    const shared = deploymentIdProblem(value);
    if (shared !== undefined)
        return shared;
    if (typeof value !== 'string')
        return undefined;
    const id = value.trim();
    if (id.length > MAX_AZURE_DEPLOYMENT_ID_LENGTH) {
        return `deployment_id is ${String(id.length)} characters. On Azure the installer names the instance's storage account {id}qrveysa, which Azure caps at 24, so it must be ${String(MAX_AZURE_DEPLOYMENT_ID_LENGTH)} or fewer.`;
    }
    return undefined;
};
/**
 * The deployment-id rule for the cloud a run is going to.
 *
 * Takes the cloud as a string, like `requestedImagePullSource`, because the
 * caller has an account key and `cloudFromAccountKey` answers only for clouds
 * an account can be registered in. Azure is one since IM-44, so an `azure:`
 * key reaches this as `'azure'` (a test reads it through the key). Anything
 * not Azure gets the shared rule, which is what every caller ran before this
 * existed.
 *
 * One function so the console, the preflight and the API cannot pick different
 * rules for one run: the arrangement `deploymentIdProblem` already had.
 */
export const deploymentIdProblemFor = (cloud, value) => forCloud(cloud, { aws: () => deploymentIdProblem(value), azure: () => azureDeploymentIdProblem(value) }, () => deploymentIdProblem(value));
//# sourceMappingURL=deployment-id.js.map